import React, { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import restaurantStaffService from '../services/restaurantStaffService'
import InviteStaffModal from './InviteStaffModal'
import {
  RestaurantStaffMember,
  RestaurantRole,
  ROLE_LABELS,
} from '../types/restaurantStaff'
import { useTranslation } from '../hooks/use-i18n'

interface RestaurantStaffSectionProps {
  restaurantId: string
}

const ROLE_BADGE_CLASSES: Record<RestaurantRole, string> = {
  owner: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
  manager: 'bg-purple-500/20 text-purple-300 border border-purple-500/30',
  waiter: 'bg-sky-500/20 text-sky-300 border border-sky-500/30',
  chef: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
  cashier: 'bg-pink-500/20 text-pink-300 border border-pink-500/30',
}

const RestaurantStaffSection: React.FC<RestaurantStaffSectionProps> = ({ restaurantId }) => {
  const { t } = useTranslation()
  const [staff, setStaff] = useState<RestaurantStaffMember[]>([])
  const [myRole, setMyRole] = useState<RestaurantRole | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [removingUserId, setRemovingUserId] = useState<string | null>(null)

  const loadStaff = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await restaurantStaffService.listStaff(restaurantId)
      setStaff(data.staff)
      setMyRole(data.myRole)
    } catch (err: any) {
      // Non-members simply won't see the section; only surface unexpected errors.
      if (err.response?.status !== 403) {
        console.error('Failed to load staff:', err)
      }
      setMyRole(null)
    } finally {
      setIsLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    loadStaff()
  }, [loadStaff])

  const canManageStaff = myRole === 'owner' || myRole === 'manager'

  const handleRemove = async (member: RestaurantStaffMember) => {
    if (!window.confirm(t('common.removeStaffConfirm', { name: member.name }))) return
    try {
      setRemovingUserId(member.userId)
      await restaurantStaffService.removeStaff(restaurantId, member.userId)
      toast.success(t('toast.staffRemoved', { name: member.name }))
      await loadStaff()
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.staffRemoveFailed'))
    } finally {
      setRemovingUserId(null)
    }
  }

  // Hide entirely for users with no access.
  if (!isLoading && myRole === null) {
    return null
  }

  return (
    <div className="bg-[#2a2a2a] border border-white/10 rounded-3xl p-6 sm:p-8 lg:p-10 mb-6 sm:mb-8">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h2 className="text-xl sm:text-2xl font-bold text-white">{t('staff.section.title')}</h2>
        {canManageStaff && (
          <button
            type="button"
            onClick={() => setShowInviteModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-lg text-sm font-semibold transition-all duration-300"
          >
            {t('staff.section.invite')}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {staff.map((member) => (
            <div
              key={member._id}
              className="bg-[#1f1f1f] rounded-xl p-4 flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white font-semibold shrink-0">
                  {member.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate">{member.name}</p>
                  <p className="text-white/50 text-xs truncate">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${ROLE_BADGE_CLASSES[member.role]}`}
                >
                  {ROLE_LABELS[member.role]}
                </span>
                {canManageStaff && !member.isOwner && (
                  <button
                    type="button"
                    onClick={() => handleRemove(member)}
                    disabled={removingUserId === member.userId}
                    className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {removingUserId === member.userId ? t('staff.section.removing') : t('staff.section.remove')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showInviteModal && (
        <InviteStaffModal
          restaurantId={restaurantId}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false)
            loadStaff()
          }}
        />
      )}
    </div>
  )
}

export default RestaurantStaffSection
